import { GOOGLE_KEY, overridePaperInputPlaceChanged } from '../../helpers/api/google-api';
import { LitElement, css, html } from 'lit';
import { Location, PointLocationInputEvent, TopicCategory } from '../../types';
import { customElement, property, query } from 'lit/decorators';
import { delay, undefinedIfEmpty } from '../../helpers/utils';

import { Select } from '@material/mwc-select';
import { TextField } from '@material/mwc-textfield';
import { removeInertFromLocationElement } from '../../helpers/inert';

@customElement('point-input')
export class PointInput extends LitElement {
    @query('#topicCategory')
    private readonly topicCategoryElement!: Select;

    @query('#location')
    private locationElement!: any & TextField;

    @property({ type: Array })
    categories: TopicCategory[] = [];

    public get topicCategoryIsValid(): boolean {
        return this.topicCategoryElement.reportValidity();
    }

    public get topicCategoryValue(): string {
        return this.topicCategoryElement.value;
    }

    public get locationIsValid(): boolean {
        // Force location to "revalidate".
        if (this.locationElement.value?.search == '') {
            this.locationElement.value = { search: '0' };
            this.locationElement.value = { search: '' };
        }
        return !this.locationElement.invalid;
    }

    public get locationValue(): Location {
        return this.locationElement.value;
    }

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        await this.fixDefaultCenterElement();
    }

    private async fixDefaultCenterElement() {
        while (undefinedIfEmpty(this.locationElement) !== undefined) {
            await delay(10);
        }

        // Since we don't explicitly await the google key retrieval this technically
        // sets off a race condition. Practically, since the key retrieval is the first of
        // a couple API calls, it should not be a concern.
        this.locationElement.apiKey = GOOGLE_KEY;

        // Sort of hack to make the input not "red".
        this.locationElement._setInvalid(false);
    }

    protected render() {
        return html`
            <paper-input-place
                id="location"
                style="margin-bottom:10px"
                @focusout="${this.locationChanged}"
                @click="${removeInertFromLocationElement}"
                @api-loaded="${() => overridePaperInputPlaceChanged(this.locationElement)}"
                class="input"
                label="Type a Location or Address *"
                required
            >
            </paper-input-place>
            <mwc-select
                id="topicCategory"
                class="input"
                style="margin-bottom:10px"
                label="Category"
                vertical-offset="60"
                required>
                <mwc-list-item></mwc-list-item>
                ${this.categories.map(
                    (c) => html`
                    <mwc-list-item twoline value="${c.name}">
                        <span><img src="${c.icon}" style="vertical-align: sub;" alt="" width="24" height="24">&nbsp;${c.name}</span>
                        <span slot="secondary">${c.description}</div>
                    </mwc-list-item>
                `
                )}
            </mwc-select>
        `;
    }

    static get styles() {
        return css`
            .input {
                width: 100%;
                padding: auto 5px;
            }
        `;
    }

    private locationChanged() {
        const locationString: string = this.locationElement.value.search;
        const splits = locationString.split(',');

        let locationName;
        if (splits.length === 5) {
            locationName = splits[0].trim();
        }

        const event = new CustomEvent<PointLocationInputEvent>('location-changed', {
            detail: { name: locationName, location: locationString },
            bubbles: true,
            composed: true,
        });

        this.dispatchEvent(event);
    }
}
